import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import ASICDesignBannerImage from "../../../assets/technologies/tools-and-languages/uvm/uvmbannerimage.png";
import ProfileImage from "../../../assets/blogpages/person.png";
import "./ASICDesign.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i}>{part}</mark>
    ) : (
      part
    )
  );
};

const ASICDesign = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const closeOverlay = () => showPopup(false);

  // refs for scrolling
  const sectionRefs = {
    intro: useRef(null),
    steps: useRef(null),
    verification: useRef(null),
    physical: useRef(null),
    timing: useRef(null),
    power: useRef(null),
    dft: useRef(null),
    packaging: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };

  const scrollTo = (ref) =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  // commit search term
  const handleSearch = () => setSearchTerm(searchInput.trim());

  // auto-scroll to first <mark>
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade-in/out observer
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) =>
          e.isIntersecting ? e.target.classList.add("visible") : e.target.classList.remove("visible")
        ),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // popup auto-dismiss
  useEffect(() => {
    if (showPopup) {
      const t = setTimeout(() => setShowPopup(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showPopup]);

  // inside your ASICDesign() before return:
const [replyVisible, setReplyVisible] = useState({});

// toggle whether a reply form is shown for a given comment ID
const toggleReply = (id) =>
  setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

// handle reply-form submission exactly like your existing comment form
const handleReplySubmit = (e) => {
  e.preventDefault();
  setShowPopup(true);
  e.target.reset();
};

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };

  return (
    <div className="blog-post-page asic-design">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>ASIC Design</h1>
          <div className="breadcrumb">
            <Link to="/services">Services</Link> | ASIC Design
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">Search</button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    steps: "Development Phases",
                    verification: "Verification Strategies",
                    physical: "Physical Implementation",
                    timing: "Timing Closure",
                    power: "Power Optimization",
                    dft: "DFT & Testability",
                    packaging: "Packaging & I/O",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="blog-content-container">
        {/* Left */}
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={ASICDesignBannerImage}
            alt="ASIC"
          />

          <div className="post-info-row fade-section">
            <span className="post-author">By John Doe</span>
            <span className="post-category">Category: Tech</span>
            <button
              className="comment-link"
              onClick={() => scrollTo(sectionRefs.comments)}
            >
              3 Comments
            </button>
          </div>

          <h2 ref={sectionRefs.intro} className="fade-section">Introduction to ASIC Design</h2>
          <p className="fade-section">
            <Highlight
              text="Application-Specific Integrated Circuit (ASIC) design refers to the process of creating a custom microchip for a specific application, rather than a general-purpose chip. ASICs are optimized for performance, power consumption, and area efficiency, making them critical in applications such as smartphones, data centers, automotive systems, and specialized AI accelerators."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="The history of ASIC design dates back to the 1980s when gate arrays and structured ASICs enabled designers to reduce time-to-market compared to full custom VLSI design. Over the decades, advancements in electronic design automation (EDA) tools, IP cores, and process technologies have further democratized ASIC development, allowing companies of all sizes to leverage custom silicon benefits."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Key advantages of ASICs include higher performance and lower power consumption compared to field-programmable gate arrays (FPGAs) when volume justifies the non-recurring engineering costs. ASICs also provide security benefits through obfuscated design and custom encryption modules. However, the ASIC flow requires rigorous planning, verification, and collaboration across architecture, RTL, and physical design teams."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.steps} className="fade-section">Key Phases in ASIC Development</h2>
          <p className="fade-section">
            <Highlight
              text="The ASIC development lifecycle comprises several critical phases, each requiring specialized tools and expertise. An efficient workflow and robust IP reuse strategies can significantly reduce project risk and schedule." 
              query={searchTerm}
            />
          </p>
          <ul className="fade-section">
            {[
              "Specification & Architecture Definition",
              "Logic Design & RTL Coding",
              "Functional Verification & UVM Testbenches",
              "Synthesis & Timing Analysis",
              "Physical Implementation: Place & Route",
              "Sign-off: Static Timing, DRC, LVS",
              "Tape-out & Post-Silicon Validation",
            ].map((step) => (
              <li key={step}>
                <Highlight text={step} query={searchTerm} />
              </li>
            ))}
          </ul>
          <p className="fade-section">
            <Highlight
              text="Early architectural exploration and high-level synthesis (HLS) can further accelerate RTL generation for complex datapaths, especially in image processing and AI inference engines."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.verification} className="fade-section">Verification Strategies</h2>
          <p className="fade-section">
            <Highlight
              text="Verification accounts for over half of the ASIC project timeline and budget. Engineers build comprehensive test plans, UVM-based environments, and formal properties to ensure correctness under all operational modes."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Key techniques include constrained-random simulation, coverage-driven verification, assertion-based verification, and hardware-assisted acceleration for large regressions. Integrating power-aware verification and low-power modes testing early prevents costly redesigns."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.physical} className="fade-section">Physical Implementation</h2>
          <p className="fade-section">
            <Highlight
              text="Physical design begins with floorplanning, defining macros and power domains. Placement and routing follow, balancing density, timing, and signal integrity. Clock tree synthesis creates an optimized network to distribute clocks with minimal skew."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Engineers perform detailed thermal and electromigration analyses. Advanced nodes (7nm, 5nm, 3nm) introduce double patterning and EUV considerations, requiring careful attention to lithography-driven design constraints."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.timing} className="fade-section">Timing Closure & Sign-off</h2>
          <p className="fade-section">
            <Highlight
              text="Static Timing Analysis (STA) verifies that all signal paths meet setup and hold requirements across process-voltage-temperature (PVT) corners. Incremental ECOs and netlist adjustments close timing margins without sacrificing area."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Full-chip extraction and path-based analysis identify critical paths. Engineers optimize paths using buffer insertion, cell upsizing, and path split. Final sign-off includes design rule checks (DRC) and layout vs. schematic (LVS) verification."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.power} className="fade-section">Power Optimization</h2>
          <p className="fade-section">
            <Highlight
              text="Power reduction is vital for battery-powered and high-density datacenter ASICs. Dynamic power is reduced via clock gating and multi-voltage domains, while leakage power is managed through multi-threshold cells and power gating."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Power-aware verification and early activity estimation guide RTL and microarchitecture choices. DVFS (Dynamic Voltage and Frequency Scaling) strategies enable adaptive performance within thermal budgets."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.dft} className="fade-section">DFT & Testability</h2>
          <p className="fade-section">
            <Highlight
              text="Design for Test (DFT) techniques insert scan chains, built-in self-test (BIST) for memories, and boundary scan for board-level test access. These features boost fault coverage and reduce manufacturing test time."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Test pattern generation and fault simulation ensure less than 5\% defect escapes. Advanced ATPG tools target bridging and stuck-at faults across dense metal layers."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.packaging} className="fade-section">Packaging & I/O</h2>
          <p className="fade-section">
            <Highlight
              text="Modern ASICs leverage flip-chip BGA, system-in-package (SiP), and interposer-based designs. Signal integrity, power delivery network (PDN) planning, and thermal dissipation shape package choices."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="High-speed SerDes lanes, PCIe, and HBM interfaces require careful channel modeling early in the PCB layout. Co-design between silicon and board teams prevents SI/PI issues post-assembly."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.trends} className="fade-section">Future Trends in ASIC Design</h2>
          <p className="fade-section">
            <Highlight
              text="Heterogeneous integration and chiplet architectures break monolithic designs into reusable tiles, reducing die size and accelerating time-to-market. Standardized chiplet interconnects like UCIe and OpenHBI are emerging."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Machine-learning-driven EDA tools automate place-and-route and timing optimization, learning from prior projects to improve tool accuracy and reduce iteration loops."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>2 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img
                  src={ProfileImage}
                  alt="User"
                  className="comment-profile-img"
                />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Stanio Lainto</span>
                    <span className="comment-date">February 16, 2024</span>
                  </div>
                  <div className="comment-socials"></div>
                </div>
              </div>
              <p className="comment-text">
                ASIC design is fascinating! Thanks for the clear overview.
              </p>
              <button className="reply-button" onClick={() => toggleReply("stanio")}>Reply</button>
              {replyVisible["stanio"] && (
    <div className="leave-comment">
      <h3>Reply to Stanio Lainto</h3>
      <form onSubmit={handleReplySubmit}>
        <div className="form-group">
          <label>Name</label>
          <input type="text" placeholder="Your name..." required />
        </div>
        <div className="form-group">
          <label>Message</label>
          <textarea placeholder="Your reply..." required />
        </div>
        <button type="submit" className="send-message-btn">
          Reply
        </button>
      </form>
    </div>
  )}
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img
                  src={ProfileImage}
                  alt="User"
                  className="comment-profile-img"
                />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Court Henry</span>
                    <span className="comment-date">February 16, 2024</span>
                  </div>
                  <div className="comment-socials"></div>
                </div>
              </div>
              <p className="comment-text">
                Very insightful overview of the ASIC flow. Thanks for sharing!
              </p>
              <button className="reply-button" onClick={() => toggleReply("chenry")}>Reply</button>
              {replyVisible["chenry"] && (
    <div className="leave-comment">
      <h3>Reply to Court Henry</h3>
      <form onSubmit={handleReplySubmit}>
        <div className="form-group">
          <label>Name</label>
          <input type="text" placeholder="Your name..." required />
        </div>
        <div className="form-group">
          <label>Message</label>
          <textarea placeholder="Your reply..." required />
        </div>
        <button type="submit" className="send-message-btn">
          Reply
        </button>
      </form>
    </div>
  )}
              
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here..." required />
                </div>
                <button type="submit" className="send-message-btn">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Desktop Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">
              Search
            </button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction to ASIC Design",
                      steps: "Key Phases in ASIC Development",
                      verification: "Verification Strategies",
                      physical: "Physical Implementation",
                      timing: "Timing Closure & Sign-off",
                      power: "Power Optimization",
                      dft: "DFT & Testability",
                      packaging: "Packaging & I/O",
                      trends: "Future Trends in ASIC Design",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Popup */}
        {/* Thank You Popup */}
<AnimatePresence>
  {showPopup && (
    <motion.div
      className="thank-you-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={closeOverlay}
    >
      <motion.div
        className="thank-you-content"
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.8 }}
        transition={{ type: "spring", stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="checkmark-container">
          <motion.svg
            className="checkmark"
            viewBox="0 0 52 52"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1, ease: "easeInOut" }}
          >
            <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
            <path
              d="M16 27.5L23 34.5L36 19.5"
              stroke="#007bff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </motion.svg>
        </div>
        <h3>Thank you for the feedback!</h3>
        <p>We appreciate your comment.</p>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>
      </div>
    </div>
  );
};

export default ASICDesign;
